export const isOutlookPath = (pathname = window.location.pathname): boolean =>
  pathname === '/outlook' || pathname.startsWith('/outlook/');

export interface OutlookConfig {
  enabled: boolean;
  clientId: string;
  authority: string;
  scope: string;
}

export class OutlookAuthError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'OutlookAuthError';
  }
}

async function readResponse(response: Response) {
  const body = await response.json();
  if (!response.ok) {
    const detail = body.detail;
    throw new OutlookAuthError(
      (typeof detail === 'string' ? detail : detail?.message) ||
        'Microsoft sign-in failed. Please try again.',
      detail?.code,
    );
  }
  return body;
}

export async function getOutlookConfig(): Promise<OutlookConfig> {
  const config = await readResponse(
    await fetch('/api/sso/microsoft/outlook/config', { credentials: 'omit' }),
  );
  if (!config.enabled || !config.clientId || !config.authority || !config.scope) {
    throw new OutlookAuthError(
      'Outlook Microsoft sign-in is not configured. Please contact your Angel administrator.',
    );
  }
  return config;
}

// Separate from the ordinary request interceptor: that interceptor injects an Angel JWT.
export async function exchangeMicrosoftToken(
  accessToken: string,
  angelToken?: string,
): Promise<API.Login> {
  const response = await fetch(`/api/sso/microsoft/outlook/${angelToken ? 'link' : 'verify'}`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(angelToken ? { Authorization: `Bearer ${angelToken}` } : {}),
    },
    body: JSON.stringify({ accessToken }),
  });
  return readResponse(response);
}

export function parseDialogMessage(
  event: { message: string; origin?: string },
  nonce: string,
  origin: string,
): string | null {
  if (event.origin !== origin) return null;
  try {
    const data = JSON.parse(event.message);
    return data.type === 'angel-microsoft-token' &&
      data.nonce === nonce &&
      typeof data.accessToken === 'string' &&
      data.accessToken.length > 0
      ? data.accessToken
      : null;
  } catch {
    return null;
  }
}
